/**
 * Adding a rubric template's criteria to a rubric, as plain data so it can be tested without the
 * editor (tests/rubric-template-insert.spec.ts). RubricEditor applies the result as one undoable change.
 */
import type { RubricCriterion, RubricSection, TemplateCriterion } from './types'

/** A card with nothing written: no criterion and no answer to "How would you check it?". */
export const isBlank = (c: RubricCriterion) => !c.criterion.trim() && !c.evidence?.trim()

/** The next `R<n>` above every ID in `used`, so no criterion ever takes an ID that was in use. */
export function nextFreeId(used: Set<string>): string {
  let n = Math.max(0, ...[...used].map(id => /^R\d+$/.test(id) ? Number(id.slice(1)) : 0)) + 1
  while (used.has(`R${n}`)) n++
  return `R${n}`
}

/** How many criteria a template may add: blank cards are dropped first, so they don't count. */
export const templateRoom = (rubric: RubricCriterion[], max: number) => Math.max(0, max - rubric.filter(c => !isBlank(c)).length)

/**
 * The rubric and sections after adding `criteria`. Blank cards (a draft's starters, or one just
 * added) are dropped; the new criteria get fresh IDs, never one in the rubric (blank cards
 * included, so Undo brings them back blank) or in `taken` (reserved or handed out earlier), and
 * go to the end of Unsectioned, which is created when missing.
 */
export function insertTemplate({ rubric, sections, taken, criteria }: {
  rubric: RubricCriterion[]; sections: RubricSection[]; taken: Iterable<string>; criteria: TemplateCriterion[]
}): { rubric: RubricCriterion[]; sections: RubricSection[]; ids: string[] } {
  const blanks = new Set(rubric.filter(isBlank).map(c => c.criterion_id))
  const used = new Set([...rubric.map(c => c.criterion_id), ...taken])
  const added = criteria.map(c => {
    const criterion_id = nextFreeId(used)
    used.add(criterion_id)
    return { ...c, criterion_id }
  })
  const ids = added.map(c => c.criterion_id)
  const trimmed = sections.map(s => ({ ...s, criterion_ids: s.criterion_ids.filter(id => !blanks.has(id)) }))
  const next = trimmed.some(s => s.section_id === 'unsectioned')
    ? trimmed.map(s => s.section_id === 'unsectioned' ? { ...s, criterion_ids: [...s.criterion_ids, ...ids] } : s)
    : [...trimmed, { section_id: 'unsectioned', title: '', criterion_ids: ids }]
  return { rubric: [...rubric.filter(c => !blanks.has(c.criterion_id)), ...added], sections: next, ids }
}
